
export type LayoutType = 'Modern' | 'Sidebar' | 'Hero' | 'Diagonal' | 'Full' | 'University' | 'None';

export interface ColorTheme {
    primary: string;
    secondary: string;
    text: string;
    textSecondary: string;
    bg: string;
    accent: string;
}

export interface IdCardConfig {
    designId: string;
    pattern: string;
    patternOpacity: number;
    patternColor: string;
    customPrimaryColor: string | null;
    customTextColor: string | null;
    customBackground: string | null;
}

export const ID_PATTERNS = [
    { id: 'none', name: 'No Pattern' },
    { id: 'watercolor', name: 'Ink Wash' },
    { id: 'brush', name: 'Rough Brush' },
    { id: 'waves-soft', name: 'Soft Smoke' },
    { id: 'splatter', name: 'Ink Splatter' },
    { id: 'marble', name: 'Liquid Marble' },
    { id: 'crosshatch', name: 'Paint Fog' },
    { id: 'noise', name: 'Spray Noise' },
    { id: 'halftone', name: 'Halftone Fade' },
    { id: 'grid', name: 'Security Grid' },
    { id: 'dots', name: 'Micro Dots' },
    { id: 'hexagons', name: 'Hexagon Mesh' },
    { id: 'waves', name: 'Guilloche Waves' },
    { id: 'lines', name: 'Diagonal Texture' },
    { id: 'circuit', name: 'Tech Circuit' },
    { id: 'geometric', name: 'Geometric Shards' },
    { id: 'topo', name: 'Topographic Map' },
    { id: 'canvas', name: 'Canvas Weave' },
    { id: 'rosette', name: 'Banknote Rosette' },
    { id: 'spiro', name: 'Spiro Security Ring' },
    { id: 'mesh', name: 'Interference Mesh' },
    { id: 'microtext', name: 'Microtext Security' },
    { id: 'lattice', name: 'Hex Lattice' },
    { id: 'burst', name: 'Radial Burst' },
    { id: 'guilloche-dense', name: 'Dense Guilloche Star' },
    { id: 'guilloche-loop', name: 'Soft Loop Guilloche' },
    { id: 'guilloche-star', name: 'Spline Star' },
    { id: 'secure-grid', name: 'Secure Grid Interference' },
    { id: 'moire', name: 'Radial Moiré' },
    { id: 'hex-3d', name: 'Isometric 3D Cube' },
    { id: 'microtext-diag', name: 'Diagonal Microtext' },
    { id: 'fibers', name: 'Security Fibers' },
    { id: 'circuit-dense', name: 'High-Density Circuit' },
    { id: 'optic', name: 'Optical Illusion Grid' },
];

export const ID_CARD_DESIGNS = [
    { id: 'none', name: 'None (Basic)', category: 'Basic' },
    { id: 'modern-indigo', name: 'Modern Indigo', category: 'Modern' },
    { id: 'modern-crimson', name: 'Modern Crimson', category: 'Modern' },
    { id: 'modern-teal', name: 'Modern Teal', category: 'Modern' },
    { id: 'modern-slate', name: 'Modern Slate', category: 'Modern' },
    { id: 'modern-violet', name: 'Modern Violet', category: 'Modern' },
    { id: 'modern-amber', name: 'Modern Amber', category: 'Modern' },
    { id: 'modern-emerald', name: 'Modern Emerald', category: 'Modern' },
    { id: 'modern-royal', name: 'Modern Royal', category: 'Modern' },
    { id: 'modern-sky', name: 'Modern Sky', category: 'Modern' },
    { id: 'modern-rose', name: 'Modern Rose', category: 'Modern' },
    { id: 'sidebar-navy', name: 'Sidebar Navy', category: 'Sidebar' },
    { id: 'sidebar-maroon', name: 'Sidebar Maroon', category: 'Sidebar' },
    { id: 'sidebar-forest', name: 'Sidebar Forest', category: 'Sidebar' },
    { id: 'sidebar-charcoal', name: 'Sidebar Charcoal', category: 'Sidebar' },
    { id: 'sidebar-plum', name: 'Sidebar Plum', category: 'Sidebar' },
    { id: 'sidebar-bronze', name: 'Sidebar Bronze', category: 'Sidebar' },
    { id: 'sidebar-ocean', name: 'Sidebar Ocean', category: 'Sidebar' },
    { id: 'sidebar-berry', name: 'Sidebar Berry', category: 'Sidebar' },
    { id: 'sidebar-gold', name: 'Sidebar Gold', category: 'Sidebar' },
    { id: 'sidebar-steel', name: 'Sidebar Steel', category: 'Sidebar' },
    { id: 'hero-cobalt', name: 'Hero Cobalt', category: 'Hero' },
    { id: 'hero-ruby', name: 'Hero Ruby', category: 'Hero' },
    { id: 'hero-pine', name: 'Hero Pine', category: 'Hero' },
    { id: 'hero-graphite', name: 'Hero Graphite', category: 'Hero' },
    { id: 'hero-amethyst', name: 'Hero Amethyst', category: 'Hero' },
    { id: 'hero-copper', name: 'Hero Copper', category: 'Hero' },
    { id: 'hero-azure', name: 'Hero Azure', category: 'Hero' },
    { id: 'hero-coral', name: 'Hero Coral', category: 'Hero' },
    { id: 'hero-citrine', name: 'Hero Citrine', category: 'Hero' },
    { id: 'hero-silver', name: 'Hero Silver', category: 'Hero' },
    { id: 'diagonal-midnight', name: 'Diagonal Midnight', category: 'Diagonal' },
    { id: 'diagonal-cardinal', name: 'Diagonal Cardinal', category: 'Diagonal' },
    { id: 'diagonal-moss', name: 'Diagonal Moss', category: 'Diagonal' },
    { id: 'diagonal-jet', name: 'Diagonal Jet', category: 'Diagonal' },
    { id: 'diagonal-orchid', name: 'Diagonal Orchid', category: 'Diagonal' },
    { id: 'diagonal-sand', name: 'Diagonal Sand', category: 'Diagonal' },
    { id: 'diagonal-aqua', name: 'Diagonal Aqua', category: 'Diagonal' },
    { id: 'diagonal-fuchsia', name: 'Diagonal Fuchsia', category: 'Diagonal' },
    { id: 'diagonal-lime', name: 'Diagonal Lime', category: 'Diagonal' },
    { id: 'diagonal-ash', name: 'Diagonal Ash', category: 'Diagonal' },
    { id: 'full-primary', name: 'Full Primary', category: 'Full' },
    { id: 'full-alert', name: 'Full Alert', category: 'Full' },
    { id: 'full-nature', name: 'Full Nature', category: 'Full' },
    { id: 'full-dark', name: 'Full Dark', category: 'Full' },
    { id: 'full-luxury', name: 'Full Luxury', category: 'Full' },
    { id: 'full-earth', name: 'Full Earth', category: 'Full' },
    { id: 'full-water', name: 'Full Water', category: 'Full' },
    { id: 'full-fire', name: 'Full Fire', category: 'Full' },
    { id: 'full-air', name: 'Full Air', category: 'Full' },
    { id: 'full-metal', name: 'Full Metal', category: 'Full' },
    { id: 'uni-harvard', name: 'Ivy Crimson (Harvard Style)', category: 'University' },
    { id: 'uni-yale', name: 'Ivy Blue (Yale Style)', category: 'University' },
    { id: 'uni-princeton', name: 'Ivy Orange (Princeton Style)', category: 'University' },
    { id: 'uni-stanford', name: 'Tech Cardinal (Stanford Style)', category: 'University' },
    { id: 'uni-mit', name: 'Tech Red (MIT Style)', category: 'University' },
];

export const COLORS: Record<string, ColorTheme> = {
    indigo: { primary: '#312e81', secondary: '#4338ca', text: '#1e1b4b', textSecondary: '#6366f1', bg: '#ffffff', accent: '#e0e7ff' },
    crimson: { primary: '#881337', secondary: '#be123c', text: '#4c0519', textSecondary: '#fb7185', bg: '#fff1f2', accent: '#ffe4e6' },
    teal: { primary: '#115e59', secondary: '#0f766e', text: '#042f2e', textSecondary: '#2dd4bf', bg: '#f0fdfa', accent: '#ccfbf1' },
    slate: { primary: '#0f172a', secondary: '#334155', text: '#020617', textSecondary: '#94a3b8', bg: '#f8fafc', accent: '#e2e8f0' },
    violet: { primary: '#4c1d95', secondary: '#6d28d9', text: '#2e1065', textSecondary: '#a78bfa', bg: '#f5f3ff', accent: '#ede9fe' },
    amber: { primary: '#78350f', secondary: '#b45309', text: '#451a03', textSecondary: '#f59e0b', bg: '#fffbeb', accent: '#fef3c7' },
    emerald: { primary: '#064e3b', secondary: '#059669', text: '#022c22', textSecondary: '#34d399', bg: '#ecfdf5', accent: '#d1fae5' },
    royal: { primary: '#1e3a8a', secondary: '#2563eb', text: '#172554', textSecondary: '#60a5fa', bg: '#eff6ff', accent: '#dbeafe' },
    sky: { primary: '#0c4a6e', secondary: '#0284c7', text: '#082f49', textSecondary: '#38bdf8', bg: '#f0f9ff', accent: '#e0f2fe' },
    rose: { primary: '#881337', secondary: '#e11d48', text: '#4c0519', textSecondary: '#fb7185', bg: '#fff1f2', accent: '#ffe4e6' },
    navy: { primary: '#0f172a', secondary: '#1e293b', text: '#0f172a', textSecondary: '#94a3b8', bg: '#ffffff', accent: '#cbd5e1' },
    maroon: { primary: '#7f1d1d', secondary: '#991b1b', text: '#7f1d1d', textSecondary: '#fca5a5', bg: '#ffffff', accent: '#fecaca' },
    forest: { primary: '#14532d', secondary: '#166534', text: '#14532d', textSecondary: '#86efac', bg: '#ffffff', accent: '#bbf7d0' },
    charcoal: { primary: '#18181b', secondary: '#27272a', text: '#18181b', textSecondary: '#a1a1aa', bg: '#ffffff', accent: '#e4e4e7' },
    plum: { primary: '#581c87', secondary: '#6b21a8', text: '#581c87', textSecondary: '#d8b4fe', bg: '#ffffff', accent: '#e9d5ff' },
    bronze: { primary: '#431407', secondary: '#7c2d12', text: '#431407', textSecondary: '#fdba74', bg: '#ffffff', accent: '#ffedd5' },
    ocean: { primary: '#164e63', secondary: '#155e75', text: '#164e63', textSecondary: '#67e8f9', bg: '#ffffff', accent: '#cffafe' },
    berry: { primary: '#831843', secondary: '#9d174d', text: '#831843', textSecondary: '#f472b6', bg: '#ffffff', accent: '#fbcfe8' },
    gold: { primary: '#713f12', secondary: '#854d0e', text: '#422006', textSecondary: '#fde047', bg: '#ffffff', accent: '#fef08a' },
    steel: { primary: '#374151', secondary: '#4b5563', text: '#374151', textSecondary: '#9ca3af', bg: '#ffffff', accent: '#e5e7eb' },
    cobalt: { primary: '#1e40af', secondary: '#1d4ed8', text: '#1e40af', textSecondary: '#60a5fa', bg: '#ffffff', accent: '#dbeafe' },
    ruby: { primary: '#9f1239', secondary: '#be123c', text: '#881337', textSecondary: '#f43f5e', bg: '#ffffff', accent: '#ffe4e6' },
    pine: { primary: '#166534', secondary: '#15803d', text: '#14532d', textSecondary: '#4ade80', bg: '#ffffff', accent: '#dcfce7' },
    graphite: { primary: '#334155', secondary: '#475569', text: '#0f172a', textSecondary: '#94a3b8', bg: '#ffffff', accent: '#f1f5f9' },
    amethyst: { primary: '#6b21a8', secondary: '#7e22ce', text: '#581c87', textSecondary: '#c084fc', bg: '#ffffff', accent: '#f3e8ff' },
    copper: { primary: '#9a3412', secondary: '#c2410c', text: '#7c2d12', textSecondary: '#fb923c', bg: '#ffffff', accent: '#ffedd5' },
    azure: { primary: '#0369a1', secondary: '#0ea5e9', text: '#0c4a6e', textSecondary: '#7dd3fc', bg: '#ffffff', accent: '#e0f2fe' },
    coral: { primary: '#be185d', secondary: '#db2777', text: '#831843', textSecondary: '#f472b6', bg: '#ffffff', accent: '#fce7f3' },
    citrine: { primary: '#a16207', secondary: '#ca8a04', text: '#713f12', textSecondary: '#facc15', bg: '#ffffff', accent: '#fef9c3' },
    silver: { primary: '#475569', secondary: '#64748b', text: '#1e293b', textSecondary: '#cbd5e1', bg: '#ffffff', accent: '#f8fafc' },
    midnight: { primary: '#172554', secondary: '#1e3a8a', text: '#0f172a', textSecondary: '#60a5fa', bg: '#eff6ff', accent: '#bfdbfe' },
    cardinal: { primary: '#7f1d1d', secondary: '#991b1b', text: '#450a0a', textSecondary: '#fca5a5', bg: '#fef2f2', accent: '#fecaca' },
    moss: { primary: '#14532d', secondary: '#166534', text: '#052e16', textSecondary: '#4ade80', bg: '#f0fdf4', accent: '#bbf7d0' },
    jet: { primary: '#000000', secondary: '#18181b', text: '#000000', textSecondary: '#71717a', bg: '#f9fafb', accent: '#e5e7eb' },
    orchid: { primary: '#581c87', secondary: '#7e22ce', text: '#3b0764', textSecondary: '#d8b4fe', bg: '#faf5ff', accent: '#e9d5ff' },
    sand: { primary: '#78350f', secondary: '#b45309', text: '#451a03', textSecondary: '#f59e0b', bg: '#fffbeb', accent: '#fde68a' },
    aqua: { primary: '#0e7490', secondary: '#06b6d4', text: '#155e75', textSecondary: '#67e8f9', bg: '#ecfeff', accent: '#cffafe' },
    fuchsia: { primary: '#701a75', secondary: '#a21caf', text: '#4a044e', textSecondary: '#e879f9', bg: '#fdf4ff', accent: '#f5d0fe' },
    lime: { primary: '#3f6212', secondary: '#65a30d', text: '#1a2e05', textSecondary: '#a3e635', bg: '#f7fee7', accent: '#d9f99d' },
    ash: { primary: '#374151', secondary: '#4b5563', text: '#111827', textSecondary: '#9ca3af', bg: '#f3f4f6', accent: '#d1d5db' },
    harvard: { primary: '#A51C30', secondary: '#292929', text: '#1e1e1e', textSecondary: '#A51C30', bg: '#ffffff', accent: '#F3F4F6' },
    yale: { primary: '#00356B', secondary: '#286dc0', text: '#222222', textSecondary: '#00356B', bg: '#ffffff', accent: '#BDD6E6' },
    princeton: { primary: '#E87722', secondary: '#000000', text: '#000000', textSecondary: '#E87722', bg: '#ffffff', accent: '#F58025' },
    stanford: { primary: '#8C1515', secondary: '#4d4f53', text: '#2e2d29', textSecondary: '#8C1515', bg: '#ffffff', accent: '#B83A4B' },
    mit: { primary: '#A31F34', secondary: '#8A8B8C', text: '#000000', textSecondary: '#A31F34', bg: '#ffffff', accent: '#C2C0BF' },
    primary: { primary: '#2563eb', secondary: '#3b82f6', text: '#172554', textSecondary: '#bfdbfe', bg: '#1e40af', accent: '#60a5fa' },
    alert: { primary: '#dc2626', secondary: '#ef4444', text: '#450a0a', textSecondary: '#fecaca', bg: '#991b1b', accent: '#f87171' },
    nature: { primary: '#16a34a', secondary: '#22c55e', text: '#052e16', textSecondary: '#bbf7d0', bg: '#15803d', accent: '#4ade80' },
    dark: { primary: '#18181b', secondary: '#27272a', text: '#000000', textSecondary: '#a1a1aa', bg: '#09090b', accent: '#52525b' },
    luxury: { primary: '#d97706', secondary: '#f59e0b', text: '#451a03', textSecondary: '#fde68a', bg: '#b45309', accent: '#fbbf24' },
    earth: { primary: '#57534e', secondary: '#78716c', text: '#292524', textSecondary: '#d6d3d1', bg: '#44403c', accent: '#a8a29e' },
    water: { primary: '#0891b2', secondary: '#06b6d4', text: '#164e63', textSecondary: '#cffafe', bg: '#0e7490', accent: '#22d3ee' },
    fire: { primary: '#ea580c', secondary: '#f97316', text: '#7c2d12', textSecondary: '#ffedd5', bg: '#c2410c', accent: '#fb923c' },
    air: { primary: '#0ea5e9', secondary: '#38bdf8', text: '#082f49', textSecondary: '#e0f2fe', bg: '#0284c7', accent: '#7dd3fc' },
    metal: { primary: '#475569', secondary: '#64748b', text: '#0f172a', textSecondary: '#cbd5e1', bg: '#334155', accent: '#94a3b8' },
};

export const getIdDesign = (designId: string) => {
    if (designId === 'none' || !designId) return { layout: 'None' as LayoutType, theme: COLORS.slate };
    const [layoutKey, colorKey] = designId.split('-');

    let layout: LayoutType = 'Modern';
    if (layoutKey === 'sidebar') layout = 'Sidebar';
    if (layoutKey === 'hero') layout = 'Hero';
    if (layoutKey === 'diagonal') layout = 'Diagonal';
    if (layoutKey === 'full') layout = 'Full';
    if (layoutKey === 'uni') layout = 'University';

    const theme = COLORS[colorKey] || COLORS.indigo;
    return { layout, theme };
};

export const DEFAULT_ID_CONFIG: IdCardConfig = {
    designId: ID_CARD_DESIGNS[1].id, // modern-indigo
    pattern: ID_PATTERNS[9].id, // Grid
    patternOpacity: 0.15,
    patternColor: '#000000',
    customPrimaryColor: null,
    customTextColor: null,
    customBackground: null,
};
